import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  FileText,
  BookOpen,
  Video,
  Code2,
  ExternalLink,
  Search,
  ArrowRight,
} from "lucide-react";
import { Input } from "../components/ui/input";

const BlogDocs = () => {
  const docCategories = [
    {
      title: "Getting Started",
      icon: <BookOpen className="h-6 w-6" />,
      docs: [
        {
          title: "Quick Start Guide",
          description: "Get up and running with Cogsmith in 5 minutes",
          link: "#",
        },
        {
          title: "Installation",
          description: "Step-by-step installation for all platforms",
          link: "#",
        },
        {
          title: "Initial Configuration",
          description: "Configure your AI providers and tools",
          link: "#",
        },
      ],
    },
    {
      title: "Features",
      icon: <FileText className="h-6 w-6" />,
      docs: [
        {
          title: "Browser Automation Guide",
          description: "Master Playwright-powered browser control",
          link: "#",
        },
        {
          title: "MCP Tools Overview",
          description: "Complete guide to all 128 available tools",
          link: "#",
        },
        {
          title: "PR Review Automation",
          description: "Automate code reviews with AI assistance",
          link: "#",
        },
        {
          title: "Scheduled Tasks",
          description: "Set up recurring and one-time automated tasks",
          link: "#",
        },
        {
          title: "System Dictation",
          description: "Use global hotkey dictation with AI cleanup",
          link: "#",
        },
      ],
    },
    {
      title: "Integrations",
      icon: <Code2 className="h-6 w-6" />,
      docs: [
        {
          title: "Azure DevOps Setup",
          description: "Connect to your Azure DevOps workspace",
          link: "#",
        },
        {
          title: "SQL Database Configuration",
          description: "Connect to PostgreSQL, MySQL, and more",
          link: "#",
        },
        {
          title: "Outlook Integration",
          description: "Email and calendar integration guide",
          link: "#",
        },
        {
          title: "Desktop Commander",
          description: "Terminal automation and filesystem operations",
          link: "#",
        },
      ],
    },
  ];

  const videoTutorials = [
    {
      title: "Cogsmith Introduction",
      description: "Overview of features and capabilities",
      thumbnail: "https://images.unsplash.com/photo-1649451844931-57e22fc82de3?auto=format&fit=crop&w=400",
    },
    {
      title: "Browser Automation Tutorial",
      description: "Learn to automate web tasks with AI",
      thumbnail: "https://images.unsplash.com/photo-1669630127566-adeac5492686?auto=format&fit=crop&w=400",
    },
    {
      title: "MCP Tools Deep Dive",
      description: "Explore the powerful tool ecosystem",
      thumbnail: "https://images.unsplash.com/photo-1694903089438-bf28d4697d9a?auto=format&fit=crop&w=400",
    },
  ];

  return (
    <div className="min-h-screen pt-20 pb-20">
      <section className="py-20 bg-gradient-to-b from-purple-950/10 to-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-6 mb-12">
            <h1 className="text-5xl md:text-6xl font-bold">
              <span className="bg-gradient-to-r from-purple-400 to-yellow-400 bg-clip-text text-transparent">
                Documentation & Resources
              </span>
            </h1>
            <p className="text-xl text-foreground/70">
              Everything you need to master Cogsmith
            </p>
          </div>

          <div className="max-w-2xl mx-auto mb-12">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-foreground/40 h-5 w-5" />
              <Input
                placeholder="Search documentation..."
                className="pl-10 h-12 bg-card/50 border-border/40 focus:border-purple-500/50"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <a
              href="https://github.com/newtro/Cogsmith"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Card className="h-full group hover:shadow-xl hover:shadow-purple-500/10 transition-all duration-300 hover:-translate-y-1 bg-card/50 backdrop-blur-sm border-border/40 hover:border-purple-500/30">
                <CardContent className="p-8 text-center space-y-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-yellow-500/20 flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                    <Code2 className="h-8 w-8 text-purple-400" />
                  </div>
                  <h3 className="text-2xl font-semibold">GitHub Repository</h3>
                  <p className="text-foreground/60">
                    View source code, report issues, and contribute
                  </p>
                  <ExternalLink className="h-5 w-5 text-purple-400 mx-auto" />
                </CardContent>
              </Card>
            </a>

            <a
              href="https://github.com/newtro/Cogsmith/releases"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Card className="h-full group hover:shadow-xl hover:shadow-purple-500/10 transition-all duration-300 hover:-translate-y-1 bg-card/50 backdrop-blur-sm border-border/40 hover:border-purple-500/30">
                <CardContent className="p-8 text-center space-y-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-yellow-500/20 flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                    <FileText className="h-8 w-8 text-purple-400" />
                  </div>
                  <h3 className="text-2xl font-semibold">Release Notes</h3>
                  <p className="text-foreground/60">
                    Latest updates, features, and bug fixes
                  </p>
                  <ExternalLink className="h-5 w-5 text-purple-400 mx-auto" />
                </CardContent>
              </Card>
            </a>

            <a
              href="https://github.com/newtro/Cogsmith/blob/main/README.md"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Card className="h-full group hover:shadow-xl hover:shadow-purple-500/10 transition-all duration-300 hover:-translate-y-1 bg-card/50 backdrop-blur-sm border-border/40 hover:border-purple-500/30">
                <CardContent className="p-8 text-center space-y-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-yellow-500/20 flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                    <BookOpen className="h-8 w-8 text-purple-400" />
                  </div>
                  <h3 className="text-2xl font-semibold">Full README</h3>
                  <p className="text-foreground/60">
                    Comprehensive guide and documentation
                  </p>
                  <ExternalLink className="h-5 w-5 text-purple-400 mx-auto" />
                </CardContent>
              </Card>
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {docCategories.map((category, index) => (
              <div key={index}>
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500/20 to-yellow-500/20 flex items-center justify-center text-purple-400">
                    {category.icon}
                  </div>
                  <h2 className="text-3xl font-bold">{category.title}</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {category.docs.map((doc, docIndex) => (
                    <Card
                      key={docIndex}
                      className="group hover:shadow-lg hover:shadow-purple-500/5 transition-all duration-300 hover:-translate-y-1 bg-card/50 backdrop-blur-sm border-border/40 hover:border-purple-500/30 cursor-pointer"
                    >
                      <CardHeader>
                        <CardTitle className="text-lg flex items-start justify-between">
                          <span className="text-foreground group-hover:text-purple-400 transition-colors">
                            {doc.title}
                          </span>
                          <ArrowRight className="h-4 w-4 text-purple-400 flex-shrink-0 ml-2 group-hover:translate-x-1 transition-transform" />
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-foreground/60 text-sm">
                          {doc.description}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-background to-purple-950/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 mb-4">
              <Video className="h-4 w-4 text-purple-400" />
              <span className="text-sm text-purple-400 font-medium">
                Coming Soon
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-purple-400 to-yellow-400 bg-clip-text text-transparent">
                Video Tutorials
              </span>
            </h2>
            <p className="text-lg text-foreground/70">
              Visual guides to help you get the most out of Cogsmith
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {videoTutorials.map((video, index) => (
              <Card
                key={index}
                className="group hover:shadow-xl hover:shadow-purple-500/10 transition-all duration-300 hover:-translate-y-1 bg-card/50 backdrop-blur-sm border-border/40 hover:border-purple-500/30 overflow-hidden cursor-pointer"
              >
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-purple-500/20 backdrop-blur-sm border border-purple-500/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Video className="h-8 w-8 text-purple-400" />
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2 text-foreground group-hover:text-purple-400 transition-colors">
                    {video.title}
                  </h3>
                  <p className="text-foreground/60 text-sm">
                    {video.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogDocs;